import "@diligentcorp/atlas-web-component-icons/lib/question/index.js";
import "@diligentcorp/atlas-web-component-icons/lib/help-docs/index.js";
import "@diligentcorp/atlas-web-component-icons/lib/support/index.js";
import { msg, updateWhenLocaleChanges } from "@lit/localize";
import { css, html } from "lit";
import { classMap } from "lit/directives/class-map.js";
import { APP_HELP_ITEMS, LINKS } from "../configuration/constants.js";
import { defineComponent, isRestrictedEnv } from "../configuration/utils.js";
import { Menu } from "./Menu.js";
import { coreVar, iconButtonVar, semanticVar } from "./styles.js";
/**
 * The `atlas-gn-help-menu` component is a menu that displays links to help resources.
 *
 * @tag atlas-gn-help-menu
 * @prop {boolean} usePendo - Whether to use Pendo for the help menu.
 * @prop {HelpKey} helpLink - The key of the help link to display.
 * @prop {Links} links - The links to display in the help menu.
 * @prop {boolean} open - Whether the menu is open.
 * @fires {CustomEvent<HelpMenu>} atlas-gn:open-menu
 */
export class HelpMenu extends Menu {
    constructor() {
        super();
        this.usePendo = false;
        this.helpLink = APP_HELP_ITEMS.HOMEPAGE.key;
        this.links = {};
        updateWhenLocaleChanges(this);
    }
    renderButtonContent() {
        const usePendo = !isRestrictedEnv && this.usePendo;
        return html `
      <div class=${classMap({ "icon-container": true, "navigation-helper--with-pendo": usePendo })}>
        <dil-question-icon aria-hidden="true"></dil-question-icon>
      </div>
    `;
    }
    renderMenu() {
        if (this.usePendo) {
            return undefined;
        }
        const currentHelpLinkEntry = Object.values(APP_HELP_ITEMS).find((item) => item.key === this.helpLink);
        const helpLink = currentHelpLinkEntry
            ? this.links?.help?.[currentHelpLinkEntry.key]
            : undefined;
        if (currentHelpLinkEntry) {
            currentHelpLinkEntry.loadIcon();
        }
        const supportLink = this.links?.support;
        return html `
      <ul role="menu">
        ${helpLink && currentHelpLinkEntry
            ? this.renderMenuItem({
                label: currentHelpLinkEntry.label(),
                icon: currentHelpLinkEntry.icon(),
                url: helpLink,
            })
            : ""}
        ${supportLink
            ? this.renderMenuItem({
                label: LINKS.SUPPORT.label(),
                icon: LINKS.SUPPORT.icon(),
                url: supportLink,
            })
            : ""}
      </ul>
    `;
    }
    get menuId() {
        return "help-menu";
    }
    get menuAriaLabel() {
        return msg(`Help menu`);
    }
    get menuButtonArialLabel() {
        return msg(`Help menu button`);
    }
}
HelpMenu.properties = {
    ...Menu.properties,
    usePendo: { type: Boolean, reflect: true, attribute: "use-pendo" },
    links: { type: Object, reflect: false, attribute: "links" },
    helpLink: { type: String, reflect: true, attribute: "help-link" },
};
HelpMenu.styles = [
    Menu.styles,
    css `
      button.icon-button {
        padding: 0;
      }
      .icon-container {
        display: flex;
        padding: ${iconButtonVar("medium-padding-left")};
      }

      .navigation-helper--with-pendo {
        position: relative;
      }

      .pendo-resource-center-badge-notification-bubble {
        background-color: ${semanticVar("color-status-new-default")} !important;
        height: ${coreVar("spacing-2")} !important;
        line-height: ${coreVar("spacing-2")} !important;
        width: min-content !important;
        padding: 0 ${coreVar("spacing-0-5")} !important;
        top: ${coreVar("spacing-px")} !important;
        left: unset !important;
        right: ${coreVar("spacing-px")} !important;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .pendo-resource-center-badge-notification-bubble::before {
        display: none;
      }

      .pendo-notification-bubble-unread-count {
        font-size: 11px !important;
        font-weight: 600 !important;
        position: unset !important;
        width: unset !important;
        height: ${coreVar("spacing-2")} !important;
        white-space: nowrap !important;
      }
      ._pendo-home-view-bubble {
        background-color: ${semanticVar("color-status-new-default")} !important;
      }
    `,
];
export const HELP_MENU_TAG = "atlas-gn-help-menu";
defineComponent(HELP_MENU_TAG, HelpMenu);
